<template>
    <wrapper-features-templates-one-column
    :noWhiteBox="customHeaderTabs.find(obj => obj.selected).id === 'Converter'"
    :nonAuthenticatedFeature="nonAuthenticatedRoute"
    :hideWhiteBoxOnLoad="showRatesLoader"
    :showLoader="showRatesLoader || showConverterLoader"
    :customBreadcrumbs="customBreadcrumbs"
    :customHeaderTabs="customHeaderTabs"
    @customTabClicked="tabClickHandler"
    @backOrCloseClicked="emit('backOrCloseClicked')">
    <!-- Last Update Date -->
        <template v-if="!getMobileAppState" #tabs-slot-extra-content>
            <div v-show="!showRatesLoader" class="last-update-date">
                <icon-cycle/>{{ t('exchanges.table.lastUpdateDate', { date: parsedLastUpdateDate }) }}
            </div>
        </template>
        <!-- *********************************************
        *												 *
        *                      Content                   *
        *												 *
        ********************************************** -->
        <transition name="common-enter-animation" mode="out-in" appear>
            <!-- Yes, this v-if can be applied directly in component, but for magic reason, if it's there, the transition only triggers the first time -->
            <div v-if="!showRatesLoader" class="transition-helper-element prevent-auto-margins">
                <!-- Component Wrapper -->
                <component
                :is="selectedTabComponent"
                :nonAuthenticatedFeature="nonAuthenticatedRoute"
                :appliedFilters="appliedFilters"
                :showConverterLoader="showConverterLoader"
                :exchangeRatesTableList="getExchangeTableList"
                :converterTableList="getExchangeTableWithValue"
                :lastUpdateDate="parsedLastUpdateDate"
                @applyFilters="applyFiltersHandler"/>
            </div>
        </transition>
    </wrapper-features-templates-one-column>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import IconCycle from '@/assets/icons/cycle.svg?inline'
/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
/* Helpers */
import { getDateWithTime } from '@/assets/scripts/dateBuilders'
import { ACCOUNT_MAIN_CURRENCY } from '@/assets/constants/accounts'
import { nonAuthenticatedRoutes } from '@/router/helpers/validations'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const {
    requestExchangeTableList,
    requestNonAuthenticatedExchangeTableList,
    requestExchangeTableWithValue,
    requestNonAuthenticatedExchangeTableWithValue,
    clearExchangeTableList,
    clearExchangeTableWithValue
} = mapActions(moduleMapper.EXCHANGES)
const { getExchangeTableList, getExchangeTableWithValue, getLastUpdateDate } = mapGetters(moduleMapper.EXCHANGES)
const { getMobileAppState } = mapGetters()

/*************************************************
*                     Aux Data                   *
*************************************************/
const showRatesLoader = ref(false)
const showConverterLoader = ref(false)
const applyErrorState = ref(false)
const currentRoute = useRoute()

/* Controls if current route is non authenticated */
const nonAuthenticatedRoute = computed(() => {
    return nonAuthenticatedRoutes().has(currentRoute.path)
})

/* Parsed last update date */
const parsedLastUpdateDate = computed(() => {
    /* Added replace with regex to capitalize the month */
    return getDateWithTime(getLastUpdateDate.value).replace(/\b[a-z]/, match => match.toUpperCase())
})
/*************************************************
*               Custom Breadcrumbs Logic         *
*************************************************/
const customBreadcrumbs = computed(() => {
    return nonAuthenticatedRoute.value ? { currentArea: t('exchanges.customBreadcrumbsCurrentArea') } : null
})

/*************************************************
*                    Tabs Logic                  *
*************************************************/
const customHeaderTabs = ref([
	{
        id: 'Rates',
        label: t('exchanges.tabs.exchangeRates'),
        selected: true
    },
	{
        id: 'Converter',
        label: t('exchanges.tabs.converter'),
        selected: false
    }
])

const tabClickHandler = (event) => {
    customHeaderTabs.value.forEach(tab => {
        tab.selected = tab.id === event
    })

    /* Reseting initial filters, and calling the right request for current selected tab */
    appliedFilters.value = INITIAL_CONVERTER_FILTERS
    event === 'Rates' ? ratesRequest() : exchangeTableListWithValueRequest()
}

const selectedTabComponent = computed(() => {
    const template = customHeaderTabs.value.find(obj => obj.selected).id
	return defineAsyncComponent(() => import('@/components/features/exchanges/' + template))
})

/*************************************************
*                                                *
*                  Requests Logic                *
*                                                *
*************************************************/
const INITIAL_CONVERTER_FILTERS = {
    currency: 'AOA',
    originValue: '',
    quotationDate: new Date()
}

const appliedFilters = ref(INITIAL_CONVERTER_FILTERS)

const applyFiltersHandler = (event) => {
    appliedFilters.value = event
    exchangeTableListWithValueRequest()
}

const ratesRequest = async () => {
    cancelAllPendingRequests()

    showRatesLoader.value = true
    applyErrorState.value = false

    const payload = {
        currency: ACCOUNT_MAIN_CURRENCY,
        quotationDate: new Date()
    }

    try {
        if (nonAuthenticatedRoute.value) {
            await requestNonAuthenticatedExchangeTableList(payload)
        } else {
            await requestExchangeTableList(payload)
        }
        showRatesLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, ratesRequest)
    }
}

const exchangeTableListWithValueRequest = async () => {
    cancelAllPendingRequests()

    showConverterLoader.value = true
    applyErrorState.value = false

    const payload = {
        ...appliedFilters.value,
        currency: appliedFilters.value.currency === 'AOA' ? 'AKZ' : appliedFilters.value.currency
    }

    if (!payload.originValue.length) payload.originValue = 1
    try {
        if (nonAuthenticatedRoute.value) {
            await requestNonAuthenticatedExchangeTableWithValue(payload)
        } else {
            await requestExchangeTableWithValue(payload)
        }
        showConverterLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, exchangeTableListWithValueRequest)
    }
}

/*************************************************
*                                                *
*                  Error Handler                 *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showRatesLoader.value = false
    showConverterLoader.value = false
    applyErrorState.value = true

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

ratesRequest()

onBeforeUnmount(() => {
    clearExchangeTableList()
    clearExchangeTableWithValue()
})
</script>

<style lang="scss" scoped>
.last-update-date {
    width: fit-content;

    display: flex;
    align-items: center;
    flex-shrink: 0;

    @include H7-Regular;
    color: var(--secondary-dark-2);

    :deep(svg) {
        margin-right: 8px;
        .change-color-fill {
            fill: var(--secondary-dark-2);
        }
    }
}

.transition-helper-element {
    @include commonEnteringAnimation;
    width: 100%;
}
</style>
